import { Variable } from "@/components/InputVariableConfigurator/types";
import {
  CacheControl,
  Content,
  PromptBlueprint,
  ToolCall,
  FunctionCall,
  FunctionCall as FunctionCallType,
} from "@/types";
import { ROLES } from "@/types/prompt-blueprint";
import { IndividualRun, RunStatus } from "@/types/metadata";

export const PLAYGROUND_TYPES = {
  CHAT: "chat",
  COMPLETION: "completion",
} as const;
export type PlaygroundType = (typeof PLAYGROUND_TYPES)[keyof typeof PLAYGROUND_TYPES];

export const PLAYGROUND_SESSION_MODES = {
  BATCH: "batch",
  //NOTE Don't confuse it with PLAYGROUND_TYPES.CHAT -> this is the mode of the playground session itself
  CHAT: "chat",
} as const;
export type PlaygroundSessionModeType =
  (typeof PLAYGROUND_SESSION_MODES)[keyof typeof PLAYGROUND_SESSION_MODES];

export const PLAYGROUND_DRAWER_MODES = {
  OUTPUT: "output",
  EVALUATE: "evaluate",
} as const;
export type PlaygroundDrawerModeType =
  (typeof PLAYGROUND_DRAWER_MODES)[keyof typeof PLAYGROUND_DRAWER_MODES];

export const DEFAULT_OUTPUT_PAGE_SIZE = 5;

export const DEFAULT_MESSAGE_TYPE = "user";

export const MESSAGE_TYPES = Object.values(ROLES);

export type MessageType = (typeof ROLES)[keyof typeof ROLES];
export type RoleI = MessageType;

export interface Message {
  id: string;
  role: MessageType;
  content: Content[];
  tool_calls?: ToolCall[];
  function_call?: FunctionCall;
  tool_call_id?: string;
  name?: string;
  cache_control?: CacheControl;
}

export interface ChatMessageStatsI {
  cost?: number;
  input_tokens?: number;
  latency_ms?: number;
  output_tokens?: number;
}

/** Note currently filled by FE only on various error cases */
export interface ChatMessageErrorI {
  type: "error";
  message: string;
  verbose?: string;
}

/**
 * This is the type of the chat message that is used in the playground session chat messages API
 */
export interface ChatMessageI extends ChatMessageStatsI {
  id: string; // "{request_id}_user" or "{request_id}_assistant"
  role: MessageType; //"user" | "assistant" | "tool";
  content: Content[];
  individual_run_request_id: number;
  request_id?: number; // Only on assistant messages
  created_at: string;
  finished_at?: string;
  error?: ChatMessageErrorI;
  tool_calls?: ToolCall[];
  function_call?: FunctionCallType;
  // For tool response messages (role: "tool")
  tool_call_id?: string;
  name?: string;
}

export interface ChatStatsI {
  total_messages: number;
  total_cost: number;
  total_input_tokens: number;
  total_latency_ms: number;
  total_output_tokens: number;
}

export interface ModelParameters {
  temperature: number;
  top_p: number;
  frequency_penalty: number;
  presence_penalty: number;
  response_format?: null;
}

export const DEFAULT_MODEL_PARAMETERS: ModelParameters = {
  temperature: 1,
  top_p: 1,
  frequency_penalty: 0,
  presence_penalty: 0,
  response_format: null,
};

export const DEFAULT_MODEL_COMPLETION_PARAMETERS: ModelParameters = {
  temperature: 1,
  top_p: 1,
  frequency_penalty: 0,
  presence_penalty: 0,
};

export const DEFAULT_CHAT_MODEL: Model = {
  name: "gpt-4o",
  provider: "openai",
  parameters: DEFAULT_MODEL_PARAMETERS,
  api_type: "chat-completions",
};

export const DEFAULT_COMPLETION_MODEL: Model = {
  name: "gpt-3.5-turbo-instruct",
  provider: "openai",
  parameters: DEFAULT_MODEL_COMPLETION_PARAMETERS,
  api_type: "chat-completions",
};

export const DEFAULT_MODEL: Model = DEFAULT_CHAT_MODEL;

export const DEFAULT_MESSAGE_1: Content = {
  type: "text",
  text: "You are a helpful assistant.",
};

export const DEFAULT_MESSAGE_1_ROLE: MessageType = "system";
export const DEFAULT_MESSAGE_2_ROLE: MessageType = "user";

export const DEFAULT_MESSAGE_2: Content = {
  type: "text",
  text: "Please write a helpful tip about prompt engineering in 3 sentences or less.",
};
export interface Model {
  name: string;
  provider: string;
  parameters: ModelParameters;
  model_config_display_name?: string | null;
  display_params?: Record<string, [value: string | boolean | null]>;
  model_config_id?: number | null;
  base_model?: string | null;
  api_type?: string | null;
}

export interface ToolCallUIData {
  tool_id?: string;
  id: string;
  _id?: string;
  name: string;
  arguments: string;
  cache_control?: CacheControl;
  /** Provider-specific metadata (e.g. Gemini thought_signature). */
  provider_metadata?: Record<string, unknown> | null;
}

export interface VariableSet {
  id: string;
  name?: string;
  folder_id?: number | null;
  variables: Variable[];
}

export interface VariableSetOperation extends VariableSet {
  request_id?: number;
  message_id?: string;
  completed?: boolean;
  run?: IndividualRun;
  error?: string;
  position?: number;
}

export interface Operation {
  id: string;
  sets: Record<string, VariableSetOperation>;
  output_instance_id?: string;
  cancel_requested?: boolean;
}

export type TemplateFormat = "f-string" | "jinja2";
export const TEMPLATE_FORMATS: TemplateFormat[] = ["f-string", "jinja2"];

export interface RunGroupRequest {
  id: string;
  playground_session_id: string;
  shared_prompt_blueprint: PromptBlueprint;
  individual_run_requests: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    input_variables: Record<string, any>;
    run_group_position: number;
  }[];
  stream?: boolean;
  prompt_id?: number | null;
  prompt_version_number?: number | null;
}

export interface RunGroupResponse {
  success: boolean;
  run_group: {
    id: string;
    created_at: string;
    updated_at: string;
    playground_session_id: string;
    shared_prompt_blueprint: PromptBlueprint;
    status: RunStatus;
    is_in_progress: boolean;
    individual_run_requests: {
      id: number;
      user_id: number;
      input_variables: Record<string, unknown>;
      is_chat: boolean;
      message: Message | Message[] | null;
      content: Content[] | null;
      request_id: number | null;
      status: RunStatus;
      status_message: string | null;
      created_at: string;
      finished_at: string | null;
      workspace_id: number;
      run_group_id: string;
      run_group_position: number;
      price: number | null;
    }[];
  };
  warnings: string[];
}
